import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, RefreshCw, AlertCircle } from 'lucide-react';
import PartnerCard from '../components/PartnerCard';
import { api } from '../api/axiosClient';

const Nearby = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    requestLocation();
  }, []);

  const requestLocation = () => {
    setLoading(true);
    setError('');

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lng: longitude });
          fetchPartners(latitude, longitude);
        },
        (err) => {
          console.error('Location error:', err);
          setError('Unable to get your location. Using default location...');
          // Use default location (e.g., Bangalore)
          fetchPartners(12.9716, 77.5946);
        }
      );
    } else {
      setError('Geolocation not supported. Using default location...');
      fetchPartners(12.9716, 77.5946);
    }
  };

  const fetchPartners = async (lat, lng) => {
    try {
      const data = await api.getPartners(lat, lng);
      setPartners(data.partners);
    } catch (err) {
      console.error('Error fetching partners:', err);
      setError('Failed to load therapists. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    requestLocation();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-accent to-green-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-bold text-gray-800">
              Therapists Near You
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Find certified cleansing therapy practitioners in your area
          </p>
        </motion.div>

        {/* Refresh Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 text-primary ${loading ? 'animate-spin' : ''}`} />
            <span className="font-medium text-gray-700">Refresh Nearby</span>
          </motion.button>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </motion.div>
        )}

        {/* Loading Shimmer */}
        {loading && (
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-md animate-pulse"
              >
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 bg-gray-300 rounded-full" />
                  <div className="flex-1 space-y-3">
                    <div className="h-6 bg-gray-300 rounded w-1/2" />
                    <div className="h-4 bg-gray-300 rounded w-1/3" />
                    <div className="h-4 bg-gray-300 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Partners List */}
        {!loading && partners.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {partners.map((partner, index) => (
              <PartnerCard key={partner.id} partner={partner} index={index} />
            ))}
          </motion.div>
        )}

        {/* No Partners Found */}
        {!loading && partners.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-12 shadow-xl text-center"
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">
              No Therapists Nearby
            </h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any therapists in your area. Leave your details and we'll notify you when one becomes available.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-primary text-white px-8 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300"
            >
              Notify Me
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Nearby;
